/**
 * Frontend UI renderer for the backend-deferred `ask_user_question` tool.
 *
 * Like ApprovalCardTool.tsx, this uses makeAssistantToolUI (UI only) — the
 * tool schema is defined in Python as an ExternalToolset
 * (analysis-agent interactive_ui_tools.py). The LLM calls the tool,
 * pydantic-ai defers it, this card collects the answers, and addResult()
 * resumes the agent.
 *
 * This is intentionally self-contained: assistant-ui (assistant-v2) is
 * legacy and slated for wholesale removal, so it shares no code with the
 * nextgen AskUserQuestionCard even though the survey UI is the same.
 */
import { useMemo, useState } from "react";
import { makeAssistantToolUI } from "@assistant-ui/react";
import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import { Check, Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { cn } from "~/lib/utils";

// Mirrors the backend `ask_user_question` schema. The "Other" choice is
// synthesized client-side, so it is not part of the wire schema.
interface AskOption {
  label: string;
  description: string;
}

interface AskQuestion {
  header: string;
  question: string;
  multiSelect: boolean;
  options: AskOption[];
}

type AskUserQuestionArgs = {
  questions: AskQuestion[];
};

// Sentinel for the synthesized "Other" choice; keeps selection bookkeeping
// unambiguous against the agent's own labels.
const OTHER = "__other__";

interface QuestionState {
  selected: string[];
  otherText: string;
}

const isAnswered = (qs: QuestionState) =>
  qs.selected.some((s) => s !== OTHER) ||
  (qs.selected.includes(OTHER) && qs.otherText.trim().length > 0);

// Readable tool-result payload the agent reads back; "Other" free text is
// inlined as `Other: <text>`.
function formatAnswer(questions: AskQuestion[], state: QuestionState[]): string {
  const answers = questions.map((q, i) => {
    const { selected, otherText } = state[i];
    const labels = selected
      .filter((s) => s !== OTHER)
      .concat(selected.includes(OTHER) ? [`Other: ${otherText.trim()}`] : []);
    return { header: q.header, question: q.question, answers: labels };
  });
  return JSON.stringify(answers);
}

const AskUserQuestionRender: ToolCallMessagePartComponent<AskUserQuestionArgs, string> = ({
  args,
  result,
  status,
  addResult,
}) => {
  const [state, setState] = useState<Record<number, QuestionState>>({});
  const [submitted, setSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState("0");

  const questions = useMemo(() => {
    if (status.type === "running" || args == null) return null;
    const qs = args.questions;
    return Array.isArray(qs) && qs.length > 0 ? qs : null;
  }, [args, status.type]);

  const getQ = (i: number): QuestionState => state[i] ?? { selected: [], otherText: "" };

  if (!questions) {
    return (
      <div className="my-2 flex items-center gap-2 rounded-lg border border-gray-200 p-4 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        <span>Preparing questions...</span>
      </div>
    );
  }

  if (result != null) {
    return <AnsweredSummary result={result} />;
  }

  const toggle = (qIdx: number, label: string, multiSelect: boolean) => {
    setState((prev) => {
      const qs = prev[qIdx] ?? { selected: [], otherText: "" };
      if (multiSelect) {
        const has = qs.selected.includes(label);
        const selected = has ? qs.selected.filter((l) => l !== label) : [...qs.selected, label];
        return { ...prev, [qIdx]: { ...qs, selected } };
      }
      return { ...prev, [qIdx]: { ...qs, selected: [label] } };
    });
  };

  const setOtherText = (qIdx: number, text: string) => {
    setState((prev) => {
      const qs = prev[qIdx] ?? { selected: [], otherText: "" };
      return { ...prev, [qIdx]: { ...qs, otherText: text } };
    });
  };

  const answeredCount = questions.filter((_, i) => isAnswered(getQ(i))).length;
  const allAnswered = answeredCount === questions.length;
  const multiple = questions.length > 1;

  // Tab navigation: Next advances once the current question is answered, Back
  // is hidden on the first tab, and the last tab swaps Next for Submit.
  const activeIdx = Number(activeTab);
  const isLastTab = activeIdx === questions.length - 1;
  const currentAnswered = isAnswered(getQ(activeIdx));
  const goBack = () => setActiveTab(String(activeIdx - 1));
  const goNext = () => setActiveTab(String(activeIdx + 1));

  const handleSubmit = () => {
    if (!allAnswered || submitted) return;
    setSubmitted(true);
    addResult(
      formatAnswer(
        questions,
        questions.map((_, i) => getQ(i))
      )
    );
  };

  return (
    <div className="my-2 flex flex-col gap-3 rounded-lg border border-border bg-background p-4 shadow-sm">
      {multiple ? (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="h-auto flex-wrap justify-start">
            {questions.map((q, qIdx) => (
              <TabsTrigger key={qIdx} value={String(qIdx)} className="gap-1.5">
                <AnsweredDot answered={isAnswered(getQ(qIdx))} />
                {q.header}
              </TabsTrigger>
            ))}
          </TabsList>
          {questions.map((q, qIdx) => (
            <TabsContent key={qIdx} value={String(qIdx)} className="mt-1">
              <QuestionBlock
                question={q}
                state={getQ(qIdx)}
                disabled={submitted}
                onToggle={(label) => toggle(qIdx, label, q.multiSelect)}
                onOtherText={(text) => setOtherText(qIdx, text)}
              />
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <QuestionBlock
          question={questions[0]}
          state={getQ(0)}
          disabled={submitted}
          showHeader
          onToggle={(label) => toggle(0, label, questions[0].multiSelect)}
          onOtherText={(text) => setOtherText(0, text)}
        />
      )}
      <div className="flex items-center justify-between gap-3">
        {multiple && activeIdx > 0 ? (
          <Button variant="ghost" size="sm" onClick={goBack} disabled={submitted}>
            Back
          </Button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-3">
          {multiple && (
            <span className="text-xs text-muted-foreground">
              {answeredCount}/{questions.length} answered
            </span>
          )}
          {multiple && !isLastTab ? (
            <Button size="sm" onClick={goNext} disabled={!currentAnswered}>
              Next
            </Button>
          ) : (
            <Button size="sm" onClick={handleSubmit} disabled={!allAnswered || submitted}>
              {submitted ? "Submitting…" : "Submit"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

interface QuestionBlockProps {
  question: AskQuestion;
  state: QuestionState;
  disabled: boolean;
  showHeader?: boolean;
  onToggle: (label: string) => void;
  onOtherText: (text: string) => void;
}

const QuestionBlock = ({
  question,
  state,
  disabled,
  showHeader = false,
  onToggle,
  onOtherText,
}: QuestionBlockProps) => {
  const otherOn = state.selected.includes(OTHER);
  return (
    <div className="flex flex-col gap-2">
      {showHeader && (
        <span className="self-start rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {question.header}
        </span>
      )}
      <p className="text-sm font-medium text-foreground">{question.question}</p>
      <div className="flex flex-col gap-1.5">
        {question.options.map((opt) => (
          <OptionRow
            key={opt.label}
            label={opt.label}
            description={opt.description}
            selected={state.selected.includes(opt.label)}
            multiSelect={question.multiSelect}
            disabled={disabled}
            onClick={() => onToggle(opt.label)}
          />
        ))}
        <OptionRow
          label="Other"
          description="Provide your own answer"
          selected={otherOn}
          multiSelect={question.multiSelect}
          disabled={disabled}
          onClick={() => onToggle(OTHER)}
        />
        {otherOn && (
          <Input
            autoFocus
            value={state.otherText}
            disabled={disabled}
            placeholder="Type your answer…"
            onChange={(e) => onOtherText(e.target.value)}
            className="mt-1 h-8 text-sm"
          />
        )}
      </div>
    </div>
  );
};

const AnsweredDot = ({ answered }: { answered: boolean }) => (
  <span
    aria-hidden
    className={cn(
      "size-2 shrink-0 rounded-full",
      answered ? "bg-primary" : "border border-muted-foreground/40"
    )}
  />
);

interface OptionRowProps {
  label: string;
  description: string;
  selected: boolean;
  multiSelect: boolean;
  disabled: boolean;
  onClick: () => void;
}

const OptionRow = ({
  label,
  description,
  selected,
  multiSelect,
  disabled,
  onClick,
}: OptionRowProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-pressed={selected}
    className={cn(
      "flex items-start gap-2.5 rounded-md border px-3 py-2 text-left transition-colors",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      selected
        ? "border-primary bg-primary/5"
        : "border-border hover:bg-muted disabled:hover:bg-transparent",
      disabled && "cursor-not-allowed opacity-60"
    )}
  >
    <span
      className={cn(
        "mt-0.5 flex size-4 shrink-0 items-center justify-center border",
        multiSelect ? "rounded-[4px]" : "rounded-full",
        selected ? "border-primary bg-primary text-primary-foreground" : "border-input"
      )}
    >
      {selected && <Check className="size-3" aria-hidden />}
    </span>
    <span className="flex min-w-0 flex-col">
      <span className="text-sm font-medium text-foreground">{label}</span>
      {description && <span className="text-xs text-muted-foreground">{description}</span>}
    </span>
  </button>
);

// Read-only recap shown once answered (after submit, and on history reload).
// Parses the tool-result payload this card produced; falls back to raw text.
const AnsweredSummary = ({ result }: { result: string }) => {
  const parsed = useMemo(() => {
    try {
      const value = JSON.parse(result);
      return Array.isArray(value)
        ? (value as { header: string; question: string; answers: string[] }[])
        : null;
    } catch {
      return null;
    }
  }, [result]);

  if (!parsed) {
    return (
      <div className="my-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        {result}
      </div>
    );
  }

  return (
    <div className="my-2 flex flex-col gap-2 rounded-lg border border-border bg-muted/30 p-3">
      {parsed.map((a, i) => (
        <div key={i} className="flex flex-col gap-0.5">
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {a.header}
          </span>
          <span className="text-sm text-foreground">{a.answers.join(", ")}</span>
        </div>
      ))}
    </div>
  );
};

export const AskUserQuestionTool = makeAssistantToolUI<AskUserQuestionArgs, string>({
  toolName: "ask_user_question",
  render: AskUserQuestionRender,
});
